import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import { refusalMessage } from '../ambient/decision-identity';
import type { AmbientDeclineReasonRow } from '../ambient/decline-reasons';
import type {
  AcceptChiefComplaintBody,
  AcceptDecisionBody,
  AmbientDecisionPort,
  AmbientDecisionRow,
  ChiefComplaintResult,
  DeclineDecisionBody,
  UndoChiefComplaintBody,
  UndoDecisionBody,
} from '../ambient/decision-types';

// B1 / B6 (PROD-2596, PROD-2533) — the Scrips.Patient ambient decision endpoints (PROD-2000,
// Scrips.Patient #967/#969/#973):
//
//   POST api/Patients/{patientId}/Encounters/{encounterId}/AmbientProposalDecisions/{Accept|Decline|Undo}
//   GET  api/Patients/{patientId}/Encounters/{encounterId}/AmbientProposalDecisions/History
//   POST api/Patients/{patientId}/Encounters/{encounterId}/Ambient/ChiefComplaint/{Accept|Undo}
//
//   GET  api/AmbientDeclineReasons?includeInactive=true   (PROD-2620: the governed reason set)
//
// Every write carries the caller's ClientRequestId (minted by the panel, once per act). Errors are
// NOT caught here: they propagate to the panel, which renders them. A write that succeeds refreshes
// the history, so the decision record shows the server's rows and never a local guess.

const base = (patientId: string, encounterId: string) =>
  `/api/Patients/${encodeURIComponent(patientId)}/Encounters/${encodeURIComponent(encounterId)}`;

export const ambientDecisionHistoryKey = (patientId?: string | null, encounterId?: string | null) =>
  ['ambient-decision-history', patientId, encounterId] as const;

export function useAmbientDecisionHistory(patientId?: string | null, encounterId?: string | null) {
  return useQuery<AmbientDecisionRow[]>({
    queryKey: ambientDecisionHistoryKey(patientId, encounterId),
    enabled: !!patientId && !!encounterId,
    queryFn: async ({ signal }) =>
      (await get<AmbientDecisionRow[]>(
        `${base(patientId as string, encounterId as string)}/AmbientProposalDecisions/History`,
        { signal },
      )) ?? [],
  });
}

export const ambientDeclineReasonsKey = ['ambient-decline-reasons', 'include-inactive'] as const;

/**
 * PROD-2620 — the governed decline-reason set, retired rows included (the record resolves old
 * decisions through it; the picker filters to active). Global reference data, so one cache entry
 * for every patient, and it changes only when someone adds or retires a row.
 */
export function useAmbientDeclineReasons(enabled: boolean) {
  return useQuery<AmbientDeclineReasonRow[]>({
    queryKey: ambientDeclineReasonsKey,
    enabled,
    staleTime: 30 * 60 * 1000,
    queryFn: async ({ signal }) =>
      (await get<AmbientDeclineReasonRow[]>('/api/AmbientDeclineReasons?includeInactive=true', { signal })) ?? [],
  });
}

/**
 * The port the ambient panel persists decisions through. `undefined` until the patient and the
 * encounter are both known — the panel then offers no decision controls at all, rather than
 * controls that would post to nowhere.
 */
export function useAmbientDecisionPort(
  patientId: string | null | undefined,
  encounterId: string | null | undefined,
  currentUserId: string | undefined,
): AmbientDecisionPort | undefined {
  const queryClient = useQueryClient();
  const history = useAmbientDecisionHistory(patientId, encounterId);
  const historyData = history.data;
  const historyError = history.error ? refusalMessage(history.error) : null;
  const declineReasons = useAmbientDeclineReasons(!!patientId && !!encounterId).data;

  return useMemo(() => {
    if (!patientId || !encounterId) return undefined;
    const root = base(patientId, encounterId);
    const refresh = () =>
      queryClient.invalidateQueries({ queryKey: ambientDecisionHistoryKey(patientId, encounterId) });
    const write = async <T,>(path: string, body: unknown): Promise<T> => {
      const result = await post<T>(`${root}${path}`, body);
      void refresh();
      return result;
    };
    // The chief-complaint write changes the encounter's complaint document, which the note's CC
    // section and the sign gate read. Refetch it, or the note keeps showing no complaint.
    const complaintChanged = () =>
      queryClient.invalidateQueries({ queryKey: ['patient-complaint', patientId, encounterId] });

    return {
      history: historyData,
      historyError,
      currentUserId,
      declineReasons,
      accept: (body: AcceptDecisionBody) => write('/AmbientProposalDecisions/Accept', body),
      decline: (body: DeclineDecisionBody) => write('/AmbientProposalDecisions/Decline', body),
      undo: (body: UndoDecisionBody) => write('/AmbientProposalDecisions/Undo', body),
      acceptChiefComplaint: async (body: AcceptChiefComplaintBody) => {
        const result = await write<ChiefComplaintResult>('/Ambient/ChiefComplaint/Accept', body);
        void complaintChanged();
        return result;
      },
      undoChiefComplaint: async (body: UndoChiefComplaintBody) => {
        const result = await write<ChiefComplaintResult>('/Ambient/ChiefComplaint/Undo', body);
        void complaintChanged();
        return result;
      },
    };
  }, [patientId, encounterId, currentUserId, historyData, historyError, declineReasons, queryClient]);
}
